import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import FlyoutContainer from 'components/consumer/FlyoutContainer';
import MaterialThumbnail from 'components/consumer/MaterialThumbnail';
import FabricAdditionalAttributes from 'components/consumer/FabricAdditionalAttributes';
import RatingAndReviews from 'components/consumer/RatingAndReviews';
import IconVelvet from 'components/icons/iconVelvet';
import RatioContainer from 'components/consumer/RatioContainer';
import useKeyboardControlForTabs from 'hooks/useKeyboardControlForTabs';
import useFabricReviews from 'data-hooks/useFabricReviews';
import useUserData from 'data-hooks/useUserData';
import splitArrayIntoParts from 'commons/splitArrayIntoParts';
import useScreen from 'hooks/useScreen';
import { LEATHER_PRODUCT_OPTION_ID } from 'commons/constants';

const MaterialThumbnailGroup = ({
  className = '',
  disabledMaterialIds = [],
  enableFlyout = true,
  fabricFavProps = { data: {} },
  flashSaleOptionIds = [],
  flyoutAlign = 'left',
  flyoutVariant,
  isEditOrder,
  isModularBundleProduct = false,
  loading = false,
  materials = [],
  moreMaterialsCount,
  onClick,
  onHover,
  onMoreMaterialsClick,
  onToggleFlyOut,
  optionId,
  selectedMaterial,
  showMoreMaterialsOnDesktop = false,
  variant,
}) => {
  const [flyOutContent, setFlyOutContent] = useState({});
  const [isFlyOutVisible, setIsFlyOutVisible] = useState(false);
  const containerRef = useRef(null);
  const { isMobile } = useScreen();
  const thumbnailGroupRef = useRef(null);
  useKeyboardControlForTabs(thumbnailGroupRef);

  const newMaterials = useMemo(() => {
    if (isMobile && optionId !== LEATHER_PRODUCT_OPTION_ID) {
      if (materials.length < 7) return splitArrayIntoParts(materials, 1);
      return splitArrayIntoParts(materials, 2);
    }
    return [materials];
  }, [materials, isMobile, optionId]);

  const {
    data: { reviews, rating },
  } = useFabricReviews(flyOutContent?.materialId);

  const { isLoggedIn } = useUserData();

  const disabledMaterialsIdMap = disabledMaterialIds.reduce((acc, cur) => {
    acc[cur] = true;
    return acc;
  }, {});
  const {
    data: { favMaterials },
  } = fabricFavProps;

  const toggleFlyOut = (isVisible, optionValueId) => {
    const newTop = containerRef?.current?.getBoundingClientRect().top || 0;
    const selectedValue = materials?.find(
      ({ materialId }) => materialId === optionValueId
    );
    let content = {};
    const formattedAddlCost = cost =>
      cost < 0 ? `-$${cost * -1}` : `+$${cost}`;

    if (isVisible && selectedValue) {
      const titleInfo = (
        <>
          {isEditOrder &&
            !!selectedValue?.origAddlCost &&
            selectedValue.origAddlCost !== selectedValue.additionalCost && (
              <span className="line-through">
                {`$${selectedValue?.origAddlCost}`}
              </span>
            )}
          {!!selectedValue?.additionalCost && (
            <span
              className={classNames('PDP_flyout_AddlCost', {
                'text-red ml-1': isEditOrder,
              })}
            >
              {`${formattedAddlCost(selectedValue.additionalCost)}${
                isModularBundleProduct ? '/piece' : ''
              }`}
            </span>
          )}
        </>
      );

      const favDisabledNotice =
        selectedValue?.materialId &&
        !!favMaterials?.[selectedValue.materialId] &&
        selectedValue?.applicableForProduct === false ? (
          <span
            className={classNames('PDP_flyout_AddlCost', {
              'text-red ml-1': true,
            })}
          >
            {`This product is not available in ${selectedValue.label.trim()}`}
          </span>
        ) : (
          ''
        );

      const title = selectedValue?.label?.trim() || '';
      content = {
        materialId: optionValueId,
        top: newTop,
        subSubTitle:
          selectedValue.tier || favDisabledNotice ? (
            <>
              <FabricAdditionalAttributes
                selectedItemGroupMaterial={selectedValue.groupMaterial}
                selectedItemGroupCleaningCode={selectedValue.cleaningCode}
                isPetAndKidFriendly={!!selectedValue.pet_friendly}
                isSustainable={!!selectedValue.sustainable_fabric}
                isGreenguardGold={!!selectedValue.greenguard_fabric}
                selectedItemTier={selectedValue.tier}
                showCleaningToolTip={false}
              />
              {favDisabledNotice}
            </>
          ) : null,
        subTitle:
          reviews?.length || selectedValue.velvet ? (
            <>
              {reviews ? (
                <RatingAndReviews
                  rating={rating}
                  numberOfReviews={reviews?.length}
                />
              ) : null}

              {selectedValue.velvet ? (
                <div className="items-center flex">
                  <IconVelvet />
                  <span className="text-[.8125rem] leading-[1.4375rem] font-bold text-[#af87a1] tracking-[.013rem] pl-0.5">
                    Velvet
                  </span>
                </div>
              ) : null}
            </>
          ) : null,
        imageUrl: selectedValue?.image,
        content: selectedValue?.description,
        title,
        titleExtra:
          title && selectedValue?.exclude_from_sale ? '- Limited Edition' : '',
        titleInfo: selectedValue?.additionalCost ? titleInfo : 0,
        showFavIcon: !!(
          favMaterials &&
          !selectedValue?.applicableForProduct === false &&
          favMaterials[optionValueId]
        ),
      };
    }

    if (enableFlyout) {
      // if onToggleFlyOut is available, the parent component handles the flyout
      if (onToggleFlyOut) {
        onToggleFlyOut(content, isVisible);
      } else {
        setFlyOutContent(content);
        setIsFlyOutVisible(isVisible);
      }
    }
  };

  const onClose = () => setIsFlyOutVisible(false);

  return loading ? (
    <div
      className={classNames(`relative w-full ${className}`, {
        'max-[1023px]:[-ms-overflow-style:none] max-[1023px]:overflow-x-scroll max-[1023px]:overflow-y-hidden max-[1023px]:pl-5 max-[1023px]:relative max-[1023px]:[scrollbar-width:none] max-[1023px]:w-screen lg:max-[1023px]:h-[45px] max-[1023px]:md:pl-[3.333vw] [&::-webkit-scrollbar]:max-[1023px]:hidden':
          variant === 'PDPSelectUpholsteryUI' ||
          variant === 'PDPSelectUpholsteryUI--quickview',
        'max-[1023px]:md:h-auto max-[1023px]:md:overflow-auto max-[1023px]:md:!p-0 max-[1023px]:md:w-auto':
          variant === 'PDPSelectUpholsteryUI--quickview',
      })}
      data-xc="MaterialThumbnailGroup"
    >
      <div
        className={classNames(
          'max-[1023px]:flex max-[1023px]:max-w-full lg:grid lg:grid-cols-[repeat(auto-fill,minmax(49px,1fr))] lg:relative lg:z-0',
          {
            'max-[1023px]:md:grid max-[1023px]:md:grid-cols-[repeat(auto-fill,minmax(49px,1fr))] max-[1023px]:md:relative max-[1023px]:md:z-0':
              variant === 'PDPSelectUpholsteryUI--quickview',
          }
        )}
      >
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(id => (
          <div
            className={classNames(
              'h-full w-full max-[1023px]:flex-shrink-0 max-[1023px]:h-[59px] max-[1023px]:min-w-[59px] max-[1023px]:w-[59px]',
              {
                'max-[1023px]:md:h-full max-[1023px]:md:min-w-[auto] max-[1023px]:md:w-full':
                  variant === 'PDPSelectUpholsteryUI--quickview',
              }
            )}
            key={id}
          >
            <MaterialThumbnail loading />
          </div>
        ))}
      </div>
    </div>
  ) : (
    <div
      className={classNames(`relative w-full ${className}`, {
        'max-[1023px]:[-ms-overflow-style:none] max-[1023px]:overflow-x-scroll max-[1023px]:overflow-y-hidden max-[1023px]:pl-5 max-[1023px]:relative max-[1023px]:[scrollbar-width:none] max-[1023px]:w-screen lg:max-[1023px]:h-[45px] max-[1023px]:md:pl-[3.333vw] [&::-webkit-scrollbar]:max-[1023px]:hidden':
          variant === 'PDPSelectUpholsteryUI' ||
          variant === 'PDPSelectUpholsteryUI--quickview',
        'max-[1023px]:md:h-auto max-[1023px]:md:overflow-auto max-[1023px]:md:!p-0 max-[1023px]:md:w-auto':
          variant === 'PDPSelectUpholsteryUI--quickview',
      })}
      ref={containerRef}
    >
      {isFlyOutVisible ? (
        <FlyoutContainer
          top={flyOutContent.top}
          titleInfo={flyOutContent.titleInfo}
          subTitle={flyOutContent.subTitle}
          subSubTitle={flyOutContent.subSubTitle}
          imageUrl={flyOutContent.imageUrl}
          content={flyOutContent.content}
          title={flyOutContent.title}
          titleExtra={flyOutContent.titleExtra}
          onClose={onClose}
          isOpen={isFlyOutVisible}
          align={flyoutAlign}
          showFavIcon={flyOutContent.showFavIcon}
          isInFlashSale={
            flyOutContent?.materialId
              ? flashSaleOptionIds.includes(flyOutContent.materialId)
              : false
          }
          flyoutVariant={flyoutVariant}
        />
      ) : null}
      {Object.keys(newMaterials)?.map(key => (
        <div
          className={classNames(
            'max-[1023px]:flex max-[1023px]:max-w-full lg:grid lg:grid-cols-[repeat(auto-fill,minmax(49px,1fr))] lg:relative lg:z-0',
            {
              'max-[1023px]:md:grid max-[1023px]:md:grid-cols-[repeat(auto-fill,minmax(49px,1fr))] max-[1023px]:md:relative max-[1023px]:md:z-0':
                variant === 'PDPSelectUpholsteryUI--quickview',
            }
          )}
          ref={thumbnailGroupRef}
          key={key}
        >
          {newMaterials[key]?.map(
            ({
              image,
              label,
              materialId,
              upholsteryOptionId,
              description,
              color,
              applicableForProduct = true,
            }) => {
              const show = !(
                fabricFavProps?.data?.hideMaterialThumbnails &&
                applicableForProduct
              );
              return (
                <div
                  key={materialId}
                  className={classNames(
                    'h-full w-full max-[1023px]:flex-shrink-0 max-[1023px]:h-[59px] max-[1023px]:min-w-[59px] max-[1023px]:w-[59px]',
                    {
                      'max-[1023px]:md:h-full max-[1023px]:md:min-w-[auto] max-[1023px]:md:w-full':
                        variant === 'PDPSelectUpholsteryUI--quickview',
                      hidden: !show,
                    }
                  )}
                >
                  <MaterialThumbnail
                    {...{
                      image,
                      label,
                      materialId,
                      onClick,
                      onHover,
                      upholsteryOptionId,
                      description,
                      isSelected: materialId === selectedMaterial?.materialId,
                      isDisabled:
                        disabledMaterialsIdMap[materialId] ||
                        !applicableForProduct,
                      fabricFavProps,
                      isFavMaterial: !!favMaterials?.[materialId],
                      isUserLoggedIn: isLoggedIn,
                      color,
                      isInFlashSale: flashSaleOptionIds.includes(materialId),
                      materialApplicableForProduct: applicableForProduct,
                    }}
                    toggleFlyOut={toggleFlyOut}
                  />
                </div>
              );
            }
          )}

          {!!moreMaterialsCount && !!onMoreMaterialsClick && (
            <div
              className={classNames(
                'h-full w-full max-[1023px]:flex-shrink-0 max-[1023px]:h-[59px] max-[1023px]:min-w-[59px] max-[1023px]:w-[59px] ml-1.5 md:hidden',
                {
                  'max-[1023px]:md:h-full max-[1023px]:md:min-w-[auto] max-[1023px]:md:w-full':
                    variant === 'PDPSelectUpholsteryUI--quickview',
                  'md:block': showMoreMaterialsOnDesktop,
                }
              )}
            >
              <RatioContainer
                className="items-center flex justify-center h-full"
                tabIndex="0"
                onClick={onMoreMaterialsClick}
              >
                <div className="items-center rounded-lg flex justify-center p-[5px] relative h-[calc(100%_-_4px)] hover:cursor-pointer after:[content:''] after:block after:absolute after:rounded-[inherit] before:[content:''] before:block before:absolute before:rounded-[inherit] before:z-0 before:w-full before:h-full before:[background:linear-gradient(90deg,#fdde5c_0,#f8ab5e_16.66667%,#f56a62_33.33333%,#a176c8_50%,#759beb_66.66667%,#65beb3_83.33333%,#70db96)] after:z-[1] after:w-[calc(100%_-_4px)] after:h-[calc(100%_-_4px)] after:bg-white">
                  <span className="text-xs tracking-[.025rem] text-center z-[2] leading-[.9rem]">
                    {`${moreMaterialsCount}+ more`}
                  </span>
                </div>
              </RatioContainer>
            </div>
          )}

          <div className="flex-1 flex-shrink-0 min-w-[20px] w-5 lg:hidden" />
        </div>
      ))}
    </div>
  );
};
MaterialThumbnailGroup.propTypes = {
  disabledMaterialIds: PropTypes.arrayOf(PropTypes.number),
  enableFlyout: PropTypes.bool,
  flyoutAlign: PropTypes.oneOf(['left', 'right']),
  loading: PropTypes.bool,
  materials: PropTypes.arrayOf(PropTypes.shape(MaterialThumbnail.propTypes)),
  moreMaterialsCount: PropTypes.number,
  onClick: PropTypes.func,
  onMoreMaterialsClick: PropTypes.func,
  onToggleFlyOut: PropTypes.func,
  selectedMaterial: PropTypes.shape({
    description: PropTypes.string,
    image: PropTypes.string,
    label: PropTypes.string,
    materialId: PropTypes.number,
    upolsteryOptionId: PropTypes.number,
  }),
  fabricFavProps: PropTypes.object,
  flashSaleOptionIds: PropTypes.arrayOf(PropTypes.number),
  className: PropTypes.string,
  flyoutVariant: PropTypes.oneOf([
    'standard',
    'protectionPlan',
    'materialSwatch',
  ]),
  isEditOrder: PropTypes.bool,
  isModularBundleProduct: PropTypes.bool,
  onHover: PropTypes.func,
  showMoreMaterialsOnDesktop: PropTypes.bool,
  optionId: PropTypes.number,
  variant: PropTypes.oneOf([
    'PDPSelectUpholsteryUI',
    'PDPSelectUpholsteryUI--quickview',
  ]),
};

export default MaterialThumbnailGroup;
